/**
 * Module dependencies.
 */

import { AdvisorSteps } from 'src/types/advisor';
import { advisorMarketTypes } from 'src/types/markets';
import { cylinderTypes } from 'src/types/technologies';
import {
  orderedChemicalTypes,
  orderedCylinderTypes,
  orderedRefillableOptions,
  orderedWaterCapacityGasContentOptions
} from './ordered-types';

import { refillableOptions } from 'src/core/content-config/model-ranges';
import {
  stepPageConfig,
  stepsKeys,
  waterCapacityGasContentOptions,
  waterCapacityOtherOptions
} from './steps-options';

import { useMemo } from 'react';

/**
 * Export `advisorFiltersKeys`.
 */

export const advisorFiltersKeys = {
  butane: 'butane',
  chemicalType: 'chemical_type',
  cylinderType: 'cylinder_type',
  market: 'market',
  numberOfBallons: 'number_of_balloons',
  propane: 'propane',
  refill: 'refill',
  waterCapacity: 'advisor_water_capacity'
};

/**
 * Export `advisorFormKeys`.
 */

export const advisorFormKeys = {
  [advisorFiltersKeys.chemicalType]: 'type_of_chemical',
  [advisorFiltersKeys.cylinderType]: 'type_of_cylinder',
  [advisorFiltersKeys.market]: 'type_of_gas',
  [advisorFiltersKeys.numberOfBallons]: 'n_of_ballons',
  [advisorFiltersKeys.refill]: 'refillable',
  [advisorFiltersKeys.waterCapacity]: 'water_capacity',
  [advisorFiltersKeys.butane]: 'butane',
  [advisorFiltersKeys.propane]: 'propane'
};

/**
 * `waterCapacityGasContentStep`.
 */

const waterCapacityGasContentStep = (initialIndex = 1) => ({
  nextStep: {
    options: {
      [waterCapacityGasContentOptions.waterCapacity]: {
        nextStep: {
          options: {
            default: {
              nextStep: {
                step: stepPageConfig({
                  isLastStep: true,
                  stepIndex: initialIndex + 2,
                  stepKey: stepsKeys.results
                })
              }
            }
          },
          step: stepPageConfig({
            filterKey: advisorFiltersKeys.waterCapacity,
            stepIndex: initialIndex + 1,
            stepKey: stepsKeys.waterCapacitySlider
          })
        }
      },
      [waterCapacityGasContentOptions.gasContent]: {
        nextStep: {
          options: {
            default: {
              nextStep: {
                step: stepPageConfig({
                  isLastStep: true,
                  stepIndex: initialIndex + 2,
                  stepKey: stepsKeys.results
                })
              }
            }
          },
          step: stepPageConfig({
            stepIndex: initialIndex + 1,
            stepKey: stepsKeys.gasContentSlider
          })
        }
      }
    },
    step: stepPageConfig({
      stepIndex: initialIndex,
      stepKey: stepsKeys.waterCapacityGasContent,
      stepOptions: orderedWaterCapacityGasContentOptions
    })
  }
});

/**
 * Export `useAdvisorSteps`.
 */

export function useAdvisorSteps(): AdvisorSteps {
  return useMemo(
    () => ({
      [advisorMarketTypes.lpg]: {
        nextStep: {
          options: {
            [cylinderTypes.lightweight]: waterCapacityGasContentStep(2),
            [cylinderTypes.traditional]: waterCapacityGasContentStep(2)
          },
          step: stepPageConfig({
            filterKey: advisorFiltersKeys.cylinderType,
            stepIndex: 1,
            stepKey: stepsKeys.typeCylinder,
            stepOptions: orderedCylinderTypes
          })
        }
      },
      [advisorMarketTypes.refrigerants]: {
        nextStep: {
          options: {
            [refillableOptions.nonRefillable]: {
              nextStep: {
                options: {
                  default: {
                    nextStep: {
                      step: stepPageConfig({
                        isLastStep: true,
                        stepIndex: 3,
                        stepKey: stepsKeys.results
                      })
                    }
                  }
                },
                step: stepPageConfig({
                  filterKey: advisorFiltersKeys.waterCapacity,
                  hasTranslatedOptions: false,
                  stepIndex: 2,
                  stepKey: stepsKeys.waterCapacityOther,
                  stepOptions: waterCapacityOtherOptions
                })
              }
            },
            [refillableOptions.refillable]: {
              nextStep: {
                options: {
                  default: {
                    nextStep: {
                      options: {
                        default: {
                          nextStep: {
                            step: stepPageConfig({
                              isLastStep: true,
                              stepIndex: 4,
                              stepKey: stepsKeys.results
                            })
                          }
                        }
                      },
                      step: stepPageConfig({
                        filterKey: advisorFiltersKeys.waterCapacity,
                        stepIndex: 3,
                        stepKey: stepsKeys.waterCapacitySlider
                      })
                    }
                  }
                },
                step: stepPageConfig({
                  filterKey: advisorFiltersKeys.cylinderType,
                  stepIndex: 2,
                  stepKey: stepsKeys.typeCylinder,
                  stepOptions: orderedCylinderTypes
                })
              }
            }
          },
          step: stepPageConfig({
            filterKey: advisorFiltersKeys.refill,
            stepIndex: 1,
            stepKey: stepsKeys.refillable,
            stepOptions: orderedRefillableOptions
          })
        }
      },
      [advisorMarketTypes.packagedChemicals]: {
        nextStep: {
          options: {
            default: {
              nextStep: {
                options: {
                  default: {
                    nextStep: {
                      step: stepPageConfig({
                        isLastStep: true,
                        stepIndex: 3,
                        stepKey: stepsKeys.results
                      })
                    }
                  }
                },
                step: stepPageConfig({
                  filterKey: advisorFiltersKeys.waterCapacity,
                  stepIndex: 2,
                  stepKey: stepsKeys.waterCapacitySlider
                })
              }
            }
          },
          step: stepPageConfig({
            filterKey: advisorFiltersKeys.chemicalType,
            stepIndex: 1,
            stepKey: stepsKeys.typeChemical,
            stepOptions: orderedChemicalTypes
          })
        }
      },
      [advisorMarketTypes.helium]: {
        nextStep: {
          options: {
            [refillableOptions.nonRefillable]: {
              nextStep: {
                options: {
                  default: {
                    nextStep: {
                      step: stepPageConfig({
                        isLastStep: true,
                        stepIndex: 3,
                        stepKey: stepsKeys.mockedBalloonsResults
                      })
                    }
                  }
                },
                step: stepPageConfig({
                  stepIndex: 2,
                  stepKey: stepsKeys.mockedBalloons
                })
              }
            },
            [refillableOptions.refillable]: {
              nextStep: {
                options: {
                  [cylinderTypes.lightweight]: {
                    nextStep: {
                      options: {
                        default: {
                          nextStep: {
                            step: stepPageConfig({
                              isLastStep: true,
                              stepIndex: 4,
                              stepKey: stepsKeys.results
                            })
                          }
                        }
                      },
                      step: stepPageConfig({
                        filterKey: advisorFiltersKeys.numberOfBallons,
                        stepIndex: 3,
                        stepKey: stepsKeys.balloons
                      })
                    }
                  },
                  [cylinderTypes.traditional]: {
                    nextStep: {
                      options: {
                        default: {
                          nextStep: {
                            step: stepPageConfig({
                              isLastStep: true,
                              stepIndex: 4,
                              stepKey: stepsKeys.results
                            })
                          }
                        }
                      },
                      step: stepPageConfig({
                        filterKey: advisorFiltersKeys.numberOfBallons,
                        stepIndex: 3,
                        stepKey: stepsKeys.balloons
                      })
                    }
                  }
                },
                step: stepPageConfig({
                  filterKey: advisorFiltersKeys.cylinderType,
                  stepIndex: 2,
                  stepKey: stepsKeys.typeCylinder,
                  stepOptions: orderedCylinderTypes
                })
              }
            }
          },
          step: stepPageConfig({
            filterKey: advisorFiltersKeys.refill,
            stepIndex: 1,
            stepKey: stepsKeys.refillable,
            stepOptions: orderedRefillableOptions
          })
        }
      },
      [advisorMarketTypes.scba]: {
        nextStep: {
          options: {
            default: {
              nextStep: {
                step: stepPageConfig({
                  isLastStep: true,
                  stepIndex: 2,
                  stepKey: stepsKeys.results
                })
              }
            }
          },
          step: stepPageConfig({
            filterKey: advisorFiltersKeys.waterCapacity,
            stepIndex: 1,
            stepKey: stepsKeys.waterCapacity
          })
        }
      }
    }),
    []
  );
}
